import { useCallback, useEffect, useMemo, useState } from "react";
import type { ButtonProps, ListProps } from "@chakra-ui/react";
import { Divider, type DividerProps, Flex, type FlexProps, ListItem, Text, UnorderedList, useMultiStyleConfig } from "@chakra-ui/react";
import { AccessibleSelect } from "@/components/AccessibleSelect/AccessibleSelect";
import { ContentBlock } from "@/components/ContentBlock/ContentBlock";
import { ContentGroup } from "@/components/ContentGroup/ContentGroup";
import { MetricList, type MetricListProps } from "@/components/DefinitionList/MetricList/MetricList";
import { DocumentDownloadDropdown } from "@/components/DocumentDownloadDropdown/DocumentDownloadDropdown";
import { ErrorBoundary } from "@/components/ErrorBoundary/ErrorBoundary";
import { FundDetailPageHeader, type FundDetailPageHeaderProps } from "@/components/FundDetailPageHeader/FundDetailPageHeader";
import type { NavMenuItemData } from "@/components/NavMenu/NavMenuItem";
import { NavModal } from "@/components/NavModal/NavModal";
import { StickyBar } from "@/components/StickyBar/StickyBar";
import Button from "@/components/chakra/Button";
import { analyticsEmitter } from "@/services/analyticsEmitter";
import { GET } from "@/services/api/client";
import { envVar } from "@/services/env";
import { layoutEmitter } from "@/services/layoutUtils";
import { useHybridRouter } from "@/services/router/useHybridRouter";
import { htmlify, sleep } from "@/services/utils";
import type { ShareClassWithFundEndpoint } from "@/types/endpoints/ShareClassWithFundEndpoint";
import type { FundDetailLayoutComponentProps } from "@/types/fundDetailLayout";
import type { V1ShareClassSimple } from "@/types/v1";

export type FxProfileHeroPanelProps = {
	header: FundDetailPageHeaderProps;
	metricList?: MetricListProps;
	changeShareclass?: {
		heading: string;
	};
	documentsDropdown?: {
		title: string;
		externalPayload: Record<string, unknown>;
	};
};

export const FxProfileHeroPanel = ({ dashboard, generateFundDetailUrlFromProps, scrollToComponent }: FundDetailLayoutComponentProps) => {
	const { fxProfileHeroPanel: props, shareClass, fundDetailNavLinks, translations, fundDetailTopNavigation } = dashboard;
	const { header, metricList, changeShareclass, documentsDropdown } = props || {};
	const { query, push } = useHybridRouter();
	const { location, channel, language } = query;
	const { parentExternalId: fundExternalId } = shareClass;
	const styleParts = useMultiStyleConfig("FxProfileHeroPanel");
	const [isAndroid, setIsAndroid] = useState(false);

	const NAV_TOP = fundDetailTopNavigation.isEnabled === false ? 0 : "72px";

	const [activeFundDetailNavLinks, setActiveFundDetailNavLinks] = useState<NavMenuItemData[]>();
	const [loading, setLoading] = useState(false);
	const [shareClassItems, setShareClassItems] = useState<V1ShareClassSimple[]>();

	const onSectionRendered = useCallback(() => {
		setActiveFundDetailNavLinks(
			fundDetailNavLinks?.filter(({ targetSelectors }) => {
				const validTargets = targetSelectors.filter((targetSelector) => {
					return document.querySelector(targetSelector) !== null;
				});

				return validTargets.length > 0;
			})
		);
	}, [fundDetailNavLinks]);

	useEffect(() => {
		(async () => {
			try {
				if (!changeShareclass) {
					return;
				}

				setLoading(true);

				const response = await GET<ShareClassWithFundEndpoint>(`${envVar("PUBLIC_ENV__API_HOST")}/api/v1/getShareClasses`, {
					location,
					channel,
					language,
					fundExternalId
				});

				if (response.isErr) {
					setLoading(false);
					return;
				}

				const { items } = response.body;

				setShareClassItems(items.length > 1 ? items : undefined);
				setLoading(false);
			} catch (e) {
				setShareClassItems(undefined);
				setLoading(false);
			}
		})();
	}, [fundExternalId, channel, language, location, changeShareclass]);

	useEffect(() => {
		const handleSectionRendered = async () => {
			await sleep(100);
			onSectionRendered();
		};

		layoutEmitter.on("SectionRendered", handleSectionRendered);

		onSectionRendered();

		return () => {
			layoutEmitter.off("SectionRendered", handleSectionRendered);
		};
	}, [onSectionRendered]);

	const handleNavLinkClick = useCallback(
		(item: NavMenuItemData) => {
			const positions = item.targetSelectors
				.map((targetSelector) => {
					return {
						selector: targetSelector,
						y: document.querySelector(targetSelector)?.getBoundingClientRect().y
					};
				})
				.filter(({ y }) => {
					return typeof y === "number" && !isNaN(y);
				}) as { selector: string; y: number }[];

			positions.sort((a, b) => {
				return a.y - b.y;
			});

			scrollToComponent(positions[0].selector, false);
		},
		[scrollToComponent]
	);

	const scrollToAnchor = useCallback(
		(navLink: NavMenuItemData) => {
			analyticsEmitter.emit("ScrollToAnchor", navLink);
			handleNavLinkClick(navLink);
		},
		[handleNavLinkClick]
	);

	const navLinks = useMemo(() => {
		if (!activeFundDetailNavLinks) {
			return null;
		}

		return (
			<>
				{/* desktop */}
				<StickyBar
					data-testid="navLinks"
					display={{ base: isAndroid ? "flex" : "none", md: "flex" }}
					top={NAV_TOP}
					zIndex="calc(var(--chakra-zIndices-sticky) - 1)"
					{...(styleParts.stickyBar as FlexProps)}
				>
					<Flex
						as="nav"
						aria-label="Navigation"
						height="full"
						maxWidth="1100px"
						width="100%"
						{...(styleParts.navBarContainer as FlexProps)}
					>
						<UnorderedList styleType="none" {...(styleParts.navBar as ListProps)}>
							{activeFundDetailNavLinks.map((navLink, index) => {
								const { value, label } = navLink;
								const isLastItem = index === activeFundDetailNavLinks.length - 1;

								return (
									<ListItem key={value}>
										<Flex align="center" height="full">
											<Button
												variant="unstyled"
												onClick={() => {
													scrollToAnchor(navLink);
												}}
												{...(styleParts.navLink as ButtonProps)}
												tabIndex={0}
												aria-description={`Click to scroll to ${htmlify(label)}`}
											>
												{htmlify(label)}
											</Button>
											{!isLastItem && (
												<Divider
													marginLeft={8}
													aria-hidden="true"
													orientation="vertical"
													height="60%"
													{...(styleParts.navDivider as DividerProps)}
												/>
											)}
										</Flex>
									</ListItem>
								);
							})}
						</UnorderedList>
					</Flex>
				</StickyBar>

				{/* mobile */}
				<StickyBar
					data-testid="navLinks_mobile"
					display={{ base: isAndroid ? "none" : "flex", md: "none" }}
					top={NAV_TOP}
					{...(styleParts.navLinksMobile as FlexProps)}
				>
					<Flex {...(styleParts.navBar as FlexProps)}>
						<Flex marginInlineStart="auto">
							<NavModal
								buttonLabel={htmlify(translations.menu)}
								items={activeFundDetailNavLinks}
								onClick={handleNavLinkClick}
								style={styleParts.navLink}
							/>
						</Flex>
					</Flex>
				</StickyBar>
			</>
		);
	}, [activeFundDetailNavLinks, isAndroid, NAV_TOP, styleParts.stickyBar, styleParts.navBarContainer, styleParts.navBar, styleParts.navLinksMobile, styleParts.navLink, styleParts.navDivider, translations.menu, handleNavLinkClick, scrollToAnchor]);

	const shareClassOptions = useMemo(() => {
		if (!shareClassItems) {
			return;
		}

		return shareClassItems.map((item) => {
			const url = generateFundDetailUrlFromProps(item);

			return {
				...item,
				url
			};
		});
	}, [shareClassItems, generateFundDetailUrlFromProps]);

	useEffect(() => {
		setIsAndroid(/Android/i.test(navigator.userAgent));
	}, []);

	if (!props) {
		return null;
	}

	return (
		<ErrorBoundary id="fxProfileHeroPanel">
			{header?.bannerEnabled !== false && (
				<Flex
					tabIndex={-1}
					id="fxProfileHeroPanel"
					data-testid="FxProfileHeroPanel"
					direction="column"
					{...(styleParts.root as FlexProps)}
				>
					{header !== undefined && (
						<FundDetailPageHeader {...header} externalId={shareClass.externalId} translations={translations} />
					)}
				</Flex>
			)}
			{navLinks}
			{translations.fundProfilePageDisclaimer && (
				<ContentGroup>
					<Text maxWidth="1100px" width="100%" marginX="auto" fontSize={16} sx={styleParts.disclaimerText}>
						{htmlify(translations.fundProfilePageDisclaimer)}
					</Text>
				</ContentGroup>
			)}
			{(shareClassOptions || metricList) && (
				<ContentGroup alignItems={{ base: "flex-start", md: "flex-end" }} sx={styleParts.panelContentGroup}>
					{shareClassOptions && (
						<Flex flexDirection="column">
							<AccessibleSelect
								id="share-class"
								data-testid="share-class-options"
								label={htmlify(changeShareclass?.heading)}
								variant="brandOutline"
								maxWidth="275px"
								lineHeight={7}
								value={shareClass.externalId}
								onChange={(e) => {
									const value = e.target.value;
									const newShareClass = shareClassOptions.find(({ externalId }) => {
										return externalId === value;
									});
									if (!newShareClass) {
										return;
									}

									push(`/${newShareClass.externalId}`);
								}}
							>
								{shareClassOptions.map(({ externalId, shortName }) => {
									return (
										<option
											key={externalId}
											value={externalId}
											style={{ color: "black !important", backgroundColor: "white !important" }}
										>
											{shortName}
										</option>
									);
								})}
							</AccessibleSelect>
						</Flex>
					)}
					{metricList && (
						<ContentBlock sx={styleParts.metrics}>
							<MetricList {...metricList} />
						</ContentBlock>
					)}
					{documentsDropdown && (
						<ContentBlock alignItems="end" flex="unset" justifyContent="end">
							<DocumentDownloadDropdown
								translations={translations}
								shareClass={dashboard.shareClass}
								{...documentsDropdown}
							/>
						</ContentBlock>
					)}
				</ContentGroup>
			)}
		</ErrorBoundary>
	);
};
